// ---------------------------------------------------------------------------
// Retain Applicable Filters
// ---------------------------------------------------------------------------
// Filters out dimensions not applicable to the target datasource.
// Used when switching datasources to preserve relevant filters only.
// ---------------------------------------------------------------------------

import type { DiscoveryFilter } from './types'
import type { DataSourceId } from '@/components/v11/discovery/types'
import { AUDIENCE_DIMENSIONS, CONTENT_DIMENSIONS } from '@/components/v11/funnel/constants'

const ALL_DIMENSIONS = [...AUDIENCE_DIMENSIONS, ...CONTENT_DIMENSIONS]

/**
 * Retain only filters applicable to the target datasource.
 * Used when switching datasources to preserve relevant filters.
 *
 * Example:
 * - Gender filter persists from Profiles -> Content (author_gender)
 * - Industry filter does NOT persist to Comments (no author_industry param)
 *
 * @param filters - Current filters
 * @param targetDataSource - Datasource user is switching to
 * @returns Filtered array with only applicable dimensions
 *
 * @example
 * const filters = [
 *   { dimension: 'gender', ... },     // applicableTo: ['profiles', 'content', 'comments']
 *   { dimension: 'industry', ... }    // applicableTo: ['profiles']
 * ]
 *
 * retainApplicableFilters(filters, 'comments')
 * // => [{ dimension: 'gender', ... }]  (industry dropped)
 */
export function retainApplicableFilters(
  filters: readonly DiscoveryFilter[],
  targetDataSource: DataSourceId
): readonly DiscoveryFilter[] {
  return filters.filter(filter => {
    const config = ALL_DIMENSIONS.find(d => d.key === filter.dimension)

    // If no config found or no applicableTo specified, keep the filter (permissive)
    if (!config || !config.applicableTo) {
      return true
    }

    // Check if target datasource is in the applicableTo list
    return config.applicableTo.includes(targetDataSource)
  })
}
